/**
 * TriggerEditorRenderer
 *
 * JSONForms custom renderer for a business rule's trigger:
 * - OnTransition: the rule runs on every transition in its scope (C18) — a multi-select over
 *   the entity's declared state-machine transitions, stored as `{ kind, transitions: [...] }`.
 *   A pre-C18 single `fromState`/`toState` pair is read as a one-element scope. An entity may
 *   carry several state machines (Multiple-State-Machines-Per-Entity.md §6.3): the options are
 *   grouped per machine, an option is identified by its transition key (two machines may share
 *   a state pair), and a scope belongs to ONE machine — stored as `stateMachine` — so picking a
 *   transition disables the other machines' options.
 * - OnCommand: the rule runs on every command in its scope (C18 step 3) — a multi-select over
 *   the entity's declared commands, stored as `{ kind, commands: [...] }`. There is no
 *   wildcard: a rule meant to run broadly selects them all, and the runs-on surface renders
 *   that list. Offered only for an entity that declares commands, because a scope the save
 *   cannot honour must not be a choice.
 * - OnCreated / OnUpdated / OnDeleted: no further configuration.
 *
 * Note: OnValidate is NOT offered per business requirements.
 */

import { useEffect, useMemo } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CACHE_TIME } from '@/config/queryDefaults';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { stateMachineService } from '@/services/core/StateMachineService';
import { ruleService } from '@/services/core/RuleService';
import {
  ON_COMMAND,
  ON_TRANSITION,
  TransitionRef,
  TriggerValue,
  describeTransition,
  effectiveCommands,
  effectiveTransitions,
  isOnCommand,
  isOnFieldChange,
  isOnSave,
  isOnTransition,
  onCommandTrigger,
  onTransitionTrigger,
  parseTrigger,
  resolveDeclared,
  serializeTrigger,
  transitionKey,
} from '@/core/rules/triggerScope';

const TRIGGER_KINDS = [
  'OnTransition', 'OnCommand',
  'OnCreated', 'OnUpdated', 'OnDeleted', 'OnValidate', 'OnActionAvailability',
  'OnFieldChange', 'OnSave',
] as const;
type TriggerKind = (typeof TRIGGER_KINDS)[number];

/**
 * Kinds no invocation can name (C18). A rule evaluation declares a transition or a command,
 * and a rule is selected when its own scope contains one of those — there is no create,
 * update, delete, validate or action-availability door for the engine to come in through, so
 * a rule stored with one of these kinds runs nowhere. `OnValidate` and `OnActionAvailability`
 * were in the rules schema until the vocabulary was narrowed to what something evaluates;
 * rows predating that are the reason they are listed here. They stay in the union so an
 * existing row still renders its own kind instead of an empty select, but they are not
 * OFFERED, on the same rule that keeps On Command off an entity declaring no commands: a
 * scoping choice the runtime could never honour must not be a choice. The server now refuses
 * to STORE one (RuleTriggerScopeResolver), which is what makes this list read-only history.
 */
const INERT_KINDS: readonly TriggerKind[] = [
  'OnCreated', 'OnUpdated', 'OnDeleted', 'OnValidate', 'OnActionAvailability',
];

/**
 * Kinds whose enforcement is GENERATED from the DSL declaration (C19, and OnSave row rules). Unlike the inert kinds
 * these do run — at the EF save boundary, on every write path — but nobody can author one here,
 * because the guard is compiled C# emitted from the rule. Rendered, never offered, for the same
 * reason: a choice the save could not honour must not be a choice.
 */
const GENERATED_KINDS: readonly TriggerKind[] = ['OnFieldChange', 'OnSave'];

/**
 * The stored kind in this component's spelling, or undefined when it is none of the known
 * ones. A trigger reaches this renderer as whatever was written into the JSONB column — an
 * import, an older writer or a hand-edited row can differ in case — and string equality
 * against TRIGGER_KINDS dropped such a row out of every comparison at once: no option in the
 * select, no scope editor, no explanatory Alert. The vocabulary is matched case-insensitively
 * everywhere else (`isOnTransition`, `isOnCommand`, `isOnFieldChange`, and the backend's
 * resolver), so it is matched case-insensitively here too.
 */
function canonicalKind(kind: string | undefined): TriggerKind | undefined {
  return TRIGGER_KINDS.find((k) => k.toLowerCase() === kind?.toLowerCase());
}


/**
 * Tester for the TriggerEditorRenderer
 * Matches the #/properties/trigger scope
 */
export const triggerEditorTester = rankWith(
  15, // High priority to override default JSONB renderer
  (uischema) => {
    return isControl(uischema) && uischema.scope === '#/properties/trigger';
  }
);

/** One state machine of the entity as the scope picker offers it: its caption and its transitions. */
interface TransitionScopeMachine {
  /** The entity field the machine drives ("status", "paymentStatus"). */
  stateMachine: string;
  caption: string;
  transitions: TransitionRef[];
}

interface TransitionScopeProps {
  entityName: string;
  loading: boolean;
  machines: TransitionScopeMachine[];
  selected: TransitionRef[];
  onChange: (transitions: TransitionRef[]) => void;
}

/**
 * The transitions an On State Transition rule runs on (C18): a multi-select over the ones the
 * entity declares, grouped per state machine when it carries several. An option is identified by
 * its transition key — unique across the entity's machines, where a state pair is not — and a
 * stored ref without a key (a rule saved before keys existed) is matched by its pair. A scope
 * belongs to one machine: once a transition is picked, the other machines' options are disabled.
 * Selecting none is an error state on purpose — the save refuses a blank scope rather than
 * reading it as "every transition".
 */
const TransitionScope = ({ entityName, loading, machines, selected, onChange }: TransitionScopeProps) => {
  const { t } = useTranslation();
  const declared = useMemo(() => machines.flatMap((machine) => machine.transitions), [machines]);
  const declaredByKey = useMemo(() => new Map(declared.map((tr) => [transitionKey(tr), tr])), [declared]);
  const selectedDeclared = useMemo(
    () => selected.map((ref) => resolveDeclared(declared, ref)).filter((tr): tr is TransitionRef => !!tr),
    [selected, declared]
  );
  const selectedKeys = selectedDeclared.map(transitionKey);
  const grouped = machines.length > 1;
  const selectedMachine = grouped ? selectedDeclared[0]?.stateMachine : undefined;
  const selectedMachineCaption = machines.find((m) => m.stateMachine === selectedMachine)?.caption;
  // Only once the entity's transitions are known: while the query is in flight (or if it
  // failed) nothing is declared yet and every selected transition would read as undeclared.
  const orphaned = loading || declared.length === 0
    ? []
    : selected.filter((ref) => !resolveDeclared(declared, ref));

  const handleSelection = (value: string | string[]) => {
    const keys = typeof value === 'string' ? value.split(',') : value;
    onChange(keys.map((k) => declaredByKey.get(k)).filter((tr): tr is TransitionRef => !!tr));
  };

  if (loading) {
    return <Typography color="text.secondary">{t('businessRules.trigger.loadingStateMachine')}</Typography>;
  }

  if (declared.length === 0) {
    return <Alert severity="warning">{t('businessRules.trigger.noTransitions', { entity: entityName })}</Alert>;
  }

  const helperText = selected.length === 0
    ? t('businessRules.trigger.noneSelected')
    : selectedMachineCaption
      ? t('businessRules.trigger.oneMachineHelp', { count: selected.length, machine: selectedMachineCaption })
      : t('businessRules.trigger.transitionsHelp', { count: selected.length });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControl fullWidth size="small" error={selected.length === 0}>
        <InputLabel>{t('businessRules.trigger.transitions')}</InputLabel>
        <Select
          multiple
          value={selectedKeys}
          label={t('businessRules.trigger.transitions')}
          onChange={(e) => handleSelection(e.target.value)}
          renderValue={(keys) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(keys as string[]).map((k) => {
                const tr = declaredByKey.get(k);
                return <Chip key={k} size="small" label={tr ? describeTransition(tr) : k} />;
              })}
            </Box>
          )}
        >
          {machines.flatMap((machine) => [
            ...(grouped
              ? [<ListSubheader key={`machine:${machine.stateMachine}`}>{machine.caption}</ListSubheader>]
              : []),
            ...machine.transitions.map((tr) => {
              const key = transitionKey(tr);
              const otherMachine = selectedMachine !== undefined && machine.stateMachine !== selectedMachine;
              return (
                <MenuItem key={key} value={key} disabled={otherMachine}>
                  <Checkbox size="small" checked={selectedKeys.includes(key)} />
                  <ListItemText primary={describeTransition(tr)} />
                </MenuItem>
              );
            }),
          ])}
        </Select>
        <FormHelperText>{helperText}</FormHelperText>
      </FormControl>
      {orphaned.length > 0 && (
        <Alert severity="warning">
          {t('businessRules.trigger.undeclaredSelected', {
            transitions: orphaned.map(describeTransition).join(', '),
            entity: entityName,
          })}
        </Alert>
      )}
    </Box>
  );
};

interface CommandScopeProps {
  entityName: string;
  loading: boolean;
  declared: string[];
  selected: string[];
  onChange: (commands: string[]) => void;
}

/**
 * The commands an On Command rule runs on (C18 step 3): a multi-select over the ones the
 * entity declares. Selecting none is an error state on purpose — the save refuses a blank
 * scope rather than reading it as "every command", which is exactly what it used to mean.
 */
const CommandScope = ({ entityName, loading, declared, selected, onChange }: CommandScopeProps) => {
  const { t } = useTranslation();
  const declaredByKey = useMemo(
    () => new Map(declared.map((c) => [c.toUpperCase(), c])),
    [declared]
  );
  const selectedKeys = selected.map((c) => c.toUpperCase());
  // Only once the entity's commands are known: while the query is in flight nothing is
  // declared yet and every selection would read as undeclared.
  const orphaned = loading || declared.length === 0
    ? []
    : selected.filter((c) => !declaredByKey.has(c.toUpperCase()));

  const handleSelection = (value: string | string[]) => {
    const keys = typeof value === 'string' ? value.split(',') : value;
    onChange(keys.map((k) => declaredByKey.get(k)).filter((c): c is string => !!c));
  };

  if (loading) {
    return <Typography color="text.secondary">{t('businessRules.trigger.loadingCommands')}</Typography>;
  }

  if (declared.length === 0) {
    return <Alert severity="warning">{t('businessRules.trigger.noCommands', { entity: entityName })}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControl fullWidth size="small" error={selected.length === 0}>
        <InputLabel>{t('businessRules.trigger.commands')}</InputLabel>
        <Select
          multiple
          value={selectedKeys}
          label={t('businessRules.trigger.commands')}
          onChange={(e) => handleSelection(e.target.value)}
          renderValue={(keys) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(keys as string[]).map((k) => (
                <Chip key={k} size="small" label={declaredByKey.get(k) ?? k} />
              ))}
            </Box>
          )}
        >
          {declared.map((command) => {
            const key = command.toUpperCase();
            return (
              <MenuItem key={key} value={key}>
                <Checkbox size="small" checked={selectedKeys.includes(key)} />
                <ListItemText primary={command} />
              </MenuItem>
            );
          })}
        </Select>
        <FormHelperText>
          {selected.length === 0
            ? t('businessRules.trigger.noCommandsSelected')
            : t('businessRules.trigger.commandsHelp', { count: selected.length })}
        </FormHelperText>
      </FormControl>
      {orphaned.length > 0 && (
        <Alert severity="warning">
          {t('businessRules.trigger.undeclaredCommandsSelected', {
            commands: orphaned.join(', '),
            entity: entityName,
          })}
        </Alert>
      )}
    </Box>
  );
};

const TriggerEditorRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path } = props;
  const { t } = useTranslation();
  const ctx = useJsonForms();
  const formData = (ctx.core?.data as Record<string, unknown> | undefined) ?? {};

  const entityName = typeof formData.entity === 'string' ? formData.entity : undefined;

  const trigger: TriggerValue = useMemo(() => parseTrigger(data) ?? { kind: ON_TRANSITION }, [data]);
  const selected = useMemo(() => effectiveTransitions(trigger), [trigger]);
  const selectedCommands = useMemo(() => effectiveCommands(trigger), [trigger]);

  const { data: stateMachines, isLoading: smLoading } = useQuery({
    queryKey: ['state-machines', entityName],
    queryFn: () => stateMachineService.getStateMachines(entityName || ''),
    enabled: !!entityName,
    staleTime: CACHE_TIME.METADATA,
  });

  // One group per state machine of the entity, the primary first, captioned by the bound field's
  // display name — the same key the detail view labels the field with.
  const machines: TransitionScopeMachine[] = useMemo(
    () => (stateMachines ?? []).map((sm) => ({
      stateMachine: sm.stateMachine,
      caption: t(`DisplayName:${entityName}.${sm.stateMachine}`, { defaultValue: sm.stateMachine }),
      transitions: sm.transitions.map(({ from, to, key }) => ({ from, to, key, stateMachine: sm.stateMachine })),
    })),
    [stateMachines, entityName, t]
  );
  const declared: TransitionRef[] = useMemo(() => machines.flatMap((m) => m.transitions), [machines]);

  // C18 step 3: the entity's declared commands ride along on the call the Module/Entity
  // pickers already make, so the On Command scope offers exactly what the save will accept.
  const { data: contextEntities, isLoading: commandsLoading } = useQuery({
    queryKey: ['rule-context-entities'],
    queryFn: () => ruleService.getContextEntities(),
    staleTime: CACHE_TIME.METADATA,
  });

  const declaredCommands: string[] = useMemo(
    () => contextEntities?.find((e) => e.entityName === entityName)?.commands ?? [],
    [contextEntities, entityName]
  );

  // A kind whose scope this entity cannot satisfy is not offered: On Command on an entity
  // that declares none would be a picker choice the save must refuse (C18 DoD — no choice
  // is ever inert). Kept while the query is in flight so the stored kind still renders.
  // The same rule retires OnCreated/OnUpdated/OnDeleted, which no invocation names: a rule
  // already stored with one keeps seeing its kind, nobody can newly choose it.
  const storedKind = canonicalKind(trigger.kind);

  const offeredKinds = useMemo(
    () => TRIGGER_KINDS.filter((kind) => {
      if (GENERATED_KINDS.includes(kind)) return storedKind === kind;
      if (INERT_KINDS.includes(kind)) return storedKind === kind;
      return kind !== ON_COMMAND || commandsLoading || declaredCommands.length > 0 || isOnCommand(trigger);
    }),
    [commandsLoading, declaredCommands.length, trigger, storedKind]
  );

  const updateTrigger = (next: TriggerValue) => handleChange(path, serializeTrigger(next));

  const handleKindChange = (kind: TriggerKind) => {
    if (kind === ON_TRANSITION) return updateTrigger(onTransitionTrigger(selected));
    if (kind === ON_COMMAND) return updateTrigger(onCommandTrigger(selectedCommands));
    return updateTrigger({ kind });
  };

  // A trigger the author never opened is still a trigger. The control DISPLAYS "On state
  // transition" as its default, so commit it: otherwise a rule saved without touching this
  // editor stores no trigger at all, the save-time guard has nothing to refuse, and the rule
  // is created mapped to nothing — the silent no-op C18/X1 exists to close, under a helper
  // text promising the opposite. Only on a NEW rule (an existing one's stored trigger is never
  // rewritten by merely opening the form) and only once the entity declares transitions to
  // pick from — an unsatisfiable scope on an entity without a state machine would leave the
  // author unable to save at all.
  const isNewRule = !formData.id;
  const hasNoTrigger = parseTrigger(data) === null;
  useEffect(() => {
    if (isNewRule && hasNoTrigger && declared.length > 0) {
      handleChange(path, serializeTrigger({ kind: ON_TRANSITION }));
    }
  }, [isNewRule, hasNoTrigger, declared.length, handleChange, path]);

  if (!entityName) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="info">{t('businessRules.trigger.selectEntityFirst')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <FormControl fullWidth size="small">
        <InputLabel>{t('businessRules.trigger.type')}</InputLabel>
        <Select
          value={storedKind ?? ''}
          label={t('businessRules.trigger.type')}
          onChange={(e) => handleKindChange(e.target.value as TriggerKind)}
        >
          {offeredKinds.map((kind) => (
            <MenuItem key={kind} value={kind}>{t(`businessRules.trigger.kind.${kind}`)}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {isOnTransition(trigger) && (
        <TransitionScope
          entityName={entityName}
          loading={smLoading}
          machines={machines}
          selected={selected}
          onChange={(transitions) => updateTrigger(onTransitionTrigger(transitions))}
        />
      )}

      {isOnCommand(trigger) && (
        <CommandScope
          entityName={entityName}
          loading={commandsLoading}
          declared={declaredCommands}
          selected={selectedCommands}
          onChange={(commands) => updateTrigger(onCommandTrigger(commands))}
        />
      )}

      {isOnFieldChange(trigger) && (
        <Alert severity="info">
          {t('businessRules.trigger.generatedKind', {
            fields: (trigger.fields ?? []).join(', ') || '—',
          })}
        </Alert>
      )}

      {isOnSave(trigger) && (
        <Alert severity="info">{t('businessRules.trigger.generatedSaveKind')}</Alert>
      )}

      {storedKind !== undefined && INERT_KINDS.includes(storedKind) && (
        <Alert severity="warning">
          {t('businessRules.trigger.inertKind', {
            kind: t(`businessRules.trigger.kind.${trigger.kind}`),
            entity: entityName,
          })}
        </Alert>
      )}
    </Box>
  );
};

/**
 * Connected renderer with JSON Forms props
 */
export const TriggerEditorRenderer = withJsonFormsControlProps(
  TriggerEditorRendererComponent
);

/**
 * Renderer entry for registration in customRenderers array
 */
export const triggerEditorRendererEntry = {
  tester: triggerEditorTester,
  renderer: TriggerEditorRenderer,
};
